/**
 * Silueta SVG liviana del perímetro de un lote (miniatura de lista).
 * No es la planimetría del detalle: sin cotas, brújula ni simplificación de lados.
 * Con vecinos es un recorte de plano: marco fijo alrededor del lote y todo lo
 * que cae adentro se dibuja, recortado parejo contra el borde (sin huecos).
 */

const EARTH_RADIUS_M = 6371008.8;
const PADDING_RATIO = 0.16;
/**
 * Mitad del marco de contexto, en múltiplos del lado mayor del lote. Es el
 * zoom: más chico acerca. El lote ocupa `1 / (1 + 2 * CONTEXT_RATIO)` del ancho.
 */
const CONTEXT_RATIO = 0.5;
const MAX_NEIGHBORS = 80;
/** Una parcela madre o manzana entera taparía el recorte: no es contexto. */
const MAX_NEIGHBOR_SPAN_RATIO = 6;

const toRad = (deg) => (deg * Math.PI) / 180;

const sanitizeBoundary = (boundary) => {
  if (!Array.isArray(boundary)) return [];
  const pts = boundary
    .map((p) => ({ lat: Number(p?.lat), lng: Number(p?.lng) }))
    .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
  if (pts.length < 2) return pts;
  const first = pts[0];
  const last = pts[pts.length - 1];
  return (first.lat === last.lat && first.lng === last.lng) ? pts.slice(0, -1) : pts;
};

const centroid = (pts) => pts.reduce(
  (acc, p) => ({ lat: acc.lat + p.lat / pts.length, lng: acc.lng + p.lng / pts.length }),
  { lat: 0, lng: 0 },
);

const projectToLocalMeters = (pts, origin) => {
  const cosLat = Math.cos(toRad(origin.lat));
  return pts.map((p) => ({
    x: toRad(p.lng - origin.lng) * EARTH_RADIUS_M * cosLat,
    y: toRad(p.lat - origin.lat) * EARTH_RADIUS_M,
  }));
};

const bboxOf = (points) => {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  points.forEach((p) => {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  });
  return {
    minX,
    maxX,
    minY,
    maxY,
    width: Math.max(maxX - minX, 0.0001),
    height: Math.max(maxY - minY, 0.0001),
    cx: (minX + maxX) / 2,
    cy: (minY + maxY) / 2,
  };
};

const overlaps = (a, b) => (
  a.minX <= b.maxX && a.maxX >= b.minX && a.minY <= b.maxY && a.maxY >= b.minY
);

const pointsToPath = (points, originX, originY, viewH) => `${points
  .map((p, i) => {
    const x = p.x - originX;
    const y = viewH - (p.y - originY);
    return `${i === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
  })
  .join(' ')} Z`;

/**
 * Proyecta un `boundary` `{ lat, lng }[]` a un path SVG (norte arriba).
 * Sin vecinos, el marco se ajusta al lote. Con vecinos, el marco es cuadrado y
 * fijo (`CONTEXT_RATIO`) y entra **todo** el que lo toca: el borde recorta
 * parejo, como un mapa, en vez de dejar manzanas a medias.
 *
 * @param {Array<{ lat: number, lng: number }>|null|undefined} boundary
 * @param {Array<Array<{ lat: number, lng: number }>>} [neighborBoundaries]
 * @returns {{
 *   viewBox: string,
 *   path: string,
 *   neighborPaths: string[],
 *   hasContext: boolean,
 *   strokeWidth: number,
 *   neighborStrokeWidth: number,
 * }|null}
 */
export const buildLotShapeThumbnail = (boundary, neighborBoundaries = []) => {
  const clean = sanitizeBoundary(boundary);
  if (clean.length < 3) return null;

  const origin = centroid(clean);
  const mainPoints = projectToLocalMeters(clean, origin);
  const mainBox = bboxOf(mainPoints);
  const span = Math.max(mainBox.width, mainBox.height);

  // Marco cuadrado centrado en el lote: el recorte no depende de si es
  // apaisado o angosto, y el vecino de arriba pesa lo mismo que el de al lado.
  const half = (span / 2) + (span * CONTEXT_RATIO);
  const frame = {
    minX: mainBox.cx - half,
    maxX: mainBox.cx + half,
    minY: mainBox.cy - half,
    maxY: mainBox.cy + half,
  };

  const scored = [];
  if (Array.isArray(neighborBoundaries) && neighborBoundaries.length > 0) {
    neighborBoundaries.forEach((raw) => {
      const neighbor = sanitizeBoundary(raw);
      if (neighbor.length < 3) return;
      const pts = projectToLocalMeters(neighbor, origin);
      const box = bboxOf(pts);
      if (Math.max(box.width, box.height) > span * MAX_NEIGHBOR_SPAN_RATIO) return;
      if (!overlaps(box, frame)) return;
      const dx = box.cx - mainBox.cx;
      const dy = box.cy - mainBox.cy;
      scored.push({ pts, dist: (dx * dx) + (dy * dy) });
    });
    scored.sort((a, b) => a.dist - b.dist);
  }

  const hasContext = scored.length > 0;
  const fitPad = span * PADDING_RATIO;
  const originX = hasContext ? frame.minX : mainBox.minX - fitPad;
  const originY = hasContext ? frame.minY : mainBox.minY - fitPad;
  const viewW = hasContext ? (half * 2) : mainBox.width + (fitPad * 2);
  const viewH = hasContext ? (half * 2) : mainBox.height + (fitPad * 2);

  const neighborPaths = scored
    .slice(0, MAX_NEIGHBORS)
    .map((entry) => pointsToPath(entry.pts, originX, originY, viewH));

  const strokeWidth = Math.max(viewW, viewH) * 0.018;
  return {
    viewBox: `0 0 ${viewW.toFixed(2)} ${viewH.toFixed(2)}`,
    path: pointsToPath(mainPoints, originX, originY, viewH),
    neighborPaths,
    hasContext,
    strokeWidth,
    neighborStrokeWidth: strokeWidth * 0.42,
  };
};
